/**
 * Self-approved weeks — the leadership control for decision 2.
 *
 * Self-approval is allowed (a one-manager branch on a Friday night cannot stall
 * on a separation of duties nobody is there to satisfy), but the decision is
 * only defensible if the pattern is visible: one week is operational reality,
 * forty is a control that does not exist. Filterable by job and date range.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { schedulingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users2 } from 'lucide-react';

export default function SelfApprovedReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = () => {
    setLoading(true);
    schedulingAPI.selfApprovedReport({ date_from: from || undefined, date_to: to || undefined })
      .then(({ data }) => setRows(data.weeks || []))
      .catch(() => toast.error('Could not load the self-approved weeks'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    const head = ['Job', 'Week', 'Approved at', 'Approver', 'Unfilled posts'];
    const lines = rows.map((r) => [
      r.job_number || r.job_id, r.week_start_date, r.approved_at,
      r.approver_name || r.approved_by_email || '', r.open_post_count ?? '',
    ].map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'self-approved-weeks.csv';
    a.click();
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="self-approved-report">
      <div className="flex items-center gap-3">
        <Users2 className="h-6 w-6 text-amber-600" />
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-fg tracking-tight">
            Self-approved weeks
          </h1>
          <p className="text-sm text-fg-muted mt-1.5">
            Weeks submitted and approved by the same person.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-fg-muted block mb-1">From week</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid="self-approved-from" />
        </div>
        <div>
          <label className="text-xs text-fg-muted block mb-1">To week</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="self-approved-to" />
        </div>
        <Button variant="outline" onClick={load} data-testid="self-approved-apply">Apply</Button>
        <Button variant="outline" onClick={exportCsv} disabled={!rows.length}
          data-testid="self-approved-export" className="ml-auto">Export CSV</Button>
      </div>

      <div className="rounded-lg border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-fg-muted">
            <tr>
              <th className="text-left px-3 py-2">Job</th>
              <th className="text-left px-3 py-2">Week</th>
              <th className="text-left px-3 py-2">Approved</th>
              <th className="text-left px-3 py-2">By</th>
              <th className="text-right px-3 py-2">Unfilled</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-fg-muted">Loading…</td></tr>
            ) : !rows.length ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-fg-muted"
                data-testid="self-approved-empty">No self-approved weeks in this range.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-line" data-testid="self-approved-row">
                <td className="px-3 py-2">{r.job_number || r.job_id}</td>
                <td className="px-3 py-2 tabular-nums">{r.week_start_date}</td>
                <td className="px-3 py-2 tabular-nums">{(r.approved_at || '').slice(0, 10)}</td>
                <td className="px-3 py-2">{r.approver_name || r.approved_by_email}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.open_post_count ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
